import { FC, useState } from 'react';
import moment from 'moment';
import { Button } from 'reactstrap';
import AiScoreBadge from '../job-order/job-order-components/AiScoreBadge';
import RowSkeleton from './RowSkeleton';
import { CandidateRowProps, MatchBand, ScoreBand } from './types';
import { formatJobAppStatus } from './utils';

// Score-band tones — pale fill + deep text (README §Design Tokens "Status
// pills"), and a solid tone for the score bar / label text. Mirrors the
// pattern already used by ConsoleFitBadge.tsx / AiScoreBadge.tsx in this repo.
// Moderate uses the gold-100/gold-700 amber pair (README §Candidate row:
// "Moderate >= 0.55 gold #B25E09"), weak uses the muted gray-400 tone. Search
// Pool only — Batch uses MATCH_BAND_TONE below.
const BAND_TONE: Record<ScoreBand, { solid: string; bg: string; text: string; label: string }> = {
  strong: { solid: '#0FA37F', bg: '#D1FAE5', text: '#066E52', label: 'Strong' },
  moderate: { solid: '#EAA827', bg: '#FEF0D9', text: '#B25E09', label: 'Moderate' },
  weak: { solid: '#98A2B3', bg: '#F2F4F7', text: '#667085', label: 'Weak' },
};

// LLM match-band tones (Batch Matches only) — driven by candidate.explain.llmScore
// via utils.matchBand() (>=70 Strong, 50-69 Possible, <50 Not a Match).
const MATCH_BAND_TONE: Record<MatchBand, { solid: string; bg: string; text: string; label: string }> = {
  strong: { solid: '#0FA37F', bg: '#D1FAE5', text: '#066E52', label: 'Strong Match' },
  possible: { solid: '#EAA827', bg: '#FEF0D9', text: '#B25E09', label: 'Possible Match' },
  none: { solid: '#98A2B3', bg: '#F2F4F7', text: '#667085', label: 'Not a Match' },
};

const ROW_CARD_BASE_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #E2E5EA',
  borderRadius: 8,
  padding: 16,
  marginBottom: 10,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: 12,
  transition: 'box-shadow 0.15s ease',
};

const ROW_SHADOW = '0 1px 2px rgba(16,24,40,0.05)';
const ROW_SHADOW_HOVER = '0 4px 10px rgba(16,24,40,0.08)';

const RANK_COL_STYLE: React.CSSProperties = {
  width: 78,
  flexShrink: 0,
  borderRight: '1px solid #E2E5EA',
  paddingRight: 12,
};

const ACTIONS_COL_STYLE: React.CSSProperties = {
  width: 186,
  flexShrink: 0,
  borderLeft: '1px solid #E2E5EA',
  paddingLeft: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

// Skill chips: gray-100 fill, 4px radius (README §Candidate row: "skill
// chips (bg #F2F4F7 gray-100, radius 4)").
const CHIP_STYLE: React.CSSProperties = {
  display: 'inline-block',
  background: '#F2F4F7',
  color: '#4B5563',
  borderRadius: 4,
  padding: '2px 9px',
  fontSize: 11,
  fontWeight: 500,
  marginRight: 6,
  marginTop: 4,
};

const GAP_CHIP_STYLE: React.CSSProperties = {
  ...CHIP_STYLE,
  background: '#FDECEE',
  color: '#B42318',
};

// Applicant-mode JobAppStatus pill (kan-119): neutral gray-100 pill, same
// family as the skill chips — status is information here, not a score.
const STATUS_PILL_STYLE: React.CSSProperties = {
  display: 'inline-block',
  alignSelf: 'flex-start',
  background: '#F2F4F7',
  color: '#344054',
  borderRadius: 999,
  padding: '2px 10px',
  fontSize: 11,
  fontWeight: 600,
};

const MONO_STYLE: React.CSSProperties = {
  fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
};

const getInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

/**
 * A single candidate row shared by all four tabs (Batch Matches, Search Pool,
 * Shortlist, Declined). Rank + score render immediately from the list
 * payload; the main (identity) column shows a shimmer until the ATS
 * `candidatesByIds` hydration call resolves, then progressively fills in
 * seniority/years/location/skills once the recsys profile call resolves
 * (README §Candidate row; STORY-2A-SPEC rule 6). Add/Decline (Batch/Search)
 * are wired to real dispositions (Story 2b) via optional onShortlistRow/
 * onDeclineRow handlers — disabled when the caller doesn't provide them.
 * Shortlist/Declined tabs instead render Apply/Restore via dispositionMode.
 * dispositionMode 'applicants' (kan-119 "Search in: New applicants") renders
 * NO row buttons — the candidate is already a JobApplicant on the job, so the
 * actions are the /new-applicants ones (Set Status / Decline) and live in the
 * detail modal; the column shows the application date + JobAppStatus pill.
 */
const CandidateRow: FC<CandidateRowProps> = ({
  item,
  view,
  onOpenDetail,
  selectable,
  selected,
  onToggleSelect,
  dispositionMode,
  onApplyRow,
  onRestoreRow,
  onShortlistRow,
  onDeclineRow,
  onApplicantScoreUpdated,
}) => {
  const { row, ats, atsLoaded, profile, profileStatus, applicant, applicantLoaded } = item;
  const [hovered, setHovered] = useState(false);
  const tone = row.band ? BAND_TONE[row.band] : null;
  const matchTone = row.matchBand ? MATCH_BAND_TONE[row.matchBand] : null;
  const skills = profile?.attributes.skills ?? [];
  const yearsExperience = profile?.attributes.yearsExperience;
  const city = profile?.attributes.city;
  const state = profile?.attributes.state;
  const locationLabel = [city, state].filter(Boolean).join(', ');
  const explain = row.explain;
  // AI-explain is always-on for Batch now (no explainOn toggle) — show the
  // card whenever the row actually has an explain payload.
  const showExplain = view === 'batch' && !!explain;
  // Owner + last résumé update come from the ATS record (candidatesByIds), so
  // they render as soon as identity hydrates. Owner falls back to the candidate
  // stats' recruiting owner, mirroring CandidateWorkspacePanel. Résumés are
  // returned newest-first, so resume[0] is the most recent.
  const ownerName =
    ats?.recruiterOwner?.fullName ?? ats?.candidateStats?.[0]?.recruitingOwner?.fullName ?? null;
  const lastResumeDate = ats?.resume?.[0]?.createdAt ?? null;

  return (
    <div
      style={{ ...ROW_CARD_BASE_STYLE, boxShadow: hovered ? ROW_SHADOW_HOVER : ROW_SHADOW }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Rank + score column */}
      <div style={RANK_COL_STYLE}>
        {selectable && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect?.(row.candidateId)}
            aria-label={`Select ${ats?.fullName ?? 'candidate'}`}
            style={{ marginBottom: 6, cursor: 'pointer' }}
          />
        )}
        <div style={{ fontSize: 12, fontWeight: 700, color: '#98A2B3' }}>#{row.rank}</div>
        {view === 'batch' ? (
          row.judged && row.llmScore !== null ? (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: matchTone?.text }}>{matchTone?.label}</div>
              <div
                style={{
                  width: 56,
                  height: 6,
                  borderRadius: 3,
                  background: '#F2F4F7',
                  marginTop: 4,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.max(0, Math.min(100, row.llmScore))}%`,
                    height: '100%',
                    background: matchTone?.solid,
                  }}
                />
              </div>
              <div style={{ ...MONO_STYLE, fontSize: 11, color: '#667085', marginTop: 4 }}>
                {row.llmScore}/100
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#98A2B3' }}>
                {row.judged ? 'Not a Match' : 'Not scored'}
              </div>
              <div style={{ fontSize: 11, color: '#98A2B3', marginTop: 4 }}>--</div>
            </div>
          )
        ) : row.isBooleanMatch ? (
          <div style={{ marginTop: 8 }}>
            <div style={{ color: '#0FA37F', fontSize: 16, fontWeight: 700, lineHeight: 1 }}>✓</div>
            <div style={{ fontSize: 10, color: '#667085', marginTop: 4 }}>
              {row.updatedAt ? `Updated ${moment(row.updatedAt).fromNow()}` : 'Matched'}
            </div>
          </div>
        ) : tone && row.score !== null ? (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: tone.text }}>{tone.label}</div>
            <div
              style={{
                width: 56,
                height: 6,
                borderRadius: 3,
                background: '#F2F4F7',
                marginTop: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${Math.max(0, Math.min(1, row.score)) * 100}%`,
                  height: '100%',
                  background: tone.solid,
                }}
              />
            </div>
            <div style={{ ...MONO_STYLE, fontSize: 11, color: '#667085', marginTop: 4 }}>
              {row.score.toFixed(2)}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 8, fontSize: 11, color: '#98A2B3' }}>--</div>
        )}
      </div>

      {/* Main (identity) column */}
      <div style={{ flex: '1 1 220px', minWidth: 220 }}>
        {!atsLoaded ? (
          <RowSkeleton />
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#EAA827',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginRight: 10,
                }}
              >
                {getInitials(ats?.fullName ?? '')}
              </div>
              <button
                type="button"
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#E08A14',
                  cursor: 'pointer',
                }}
                onClick={() => onOpenDetail(row.candidateId)}
              >
                {ats?.fullName ?? 'Unknown candidate'}
              </button>
            </div>

            <div style={{ fontSize: 12, color: '#1F2937', marginTop: 4 }}>
              {[ats?.jobTitle, ats?.companyName].filter(Boolean).join(' · ') || '--'}
            </div>

            {lastResumeDate && (
              <div style={{ fontSize: 12, color: '#667085', marginTop: 4 }}>
                {`Résumé updated ${moment(lastResumeDate).format('MMM D, YYYY')}`}
              </div>
            )}
            {/* Always shown once the ATS record hydrates — "--" makes ownerless
                candidates explicit rather than silently omitting the line. */}
            {ats && (
              <div style={{ fontSize: 12, color: '#667085', marginTop: 4 }}>
                {`Candidate Owner: ${ownerName ?? '--'}`}
              </div>
            )}

            {profileStatus === 'loading' && (
              <div style={{ fontSize: 11, color: '#98A2B3', marginTop: 4 }}>loading match details…</div>
            )}
            {profileStatus === 'removed' && (
              <div style={{ fontSize: 11, color: '#B42318', marginTop: 4 }}>
                This candidate is no longer in the recsys index.
              </div>
            )}
            {profileStatus === 'error' && (
              <div style={{ fontSize: 11, color: '#98A2B3', marginTop: 4 }}>
                Match details unavailable — showing candidate record only.
              </div>
            )}
            {profileStatus === 'loaded' && (
              <div style={{ fontSize: 12, color: '#667085', marginTop: 4 }}>
                {[locationLabel || null, yearsExperience !== null && yearsExperience !== undefined
                  ? `${yearsExperience} yrs experience`
                  : null]
                  .filter(Boolean)
                  .join(' · ') || '--'}
              </div>
            )}

            {profileStatus === 'loaded' && skills.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {skills.slice(0, 5).map((skill, idx) => (
                  <span key={`${skill}-${idx}`} style={CHIP_STYLE}>
                    {skill}
                  </span>
                ))}
              </div>
            )}

            {showExplain && (
              <div
                style={{
                  marginTop: 10,
                  background: '#FEF0D9',
                  border: '1px solid #FBDBA0',
                  borderRadius: 6,
                  padding: '10px 12px',
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 700, color: '#B25E09', textTransform: 'uppercase' }}>
                  AI match
                </div>
                {explain?.status === 'ok' ? (
                  <>
                    {explain.llmScore !== null && (
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1F2937', marginTop: 2 }}>
                        LLM {explain.llmScore}/100
                      </div>
                    )}
                    {explain.explanation && (
                      <div style={{ fontSize: 12, color: '#1F2937', marginTop: 4 }}>{explain.explanation}</div>
                    )}
                    {explain.gaps.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#B42318', marginRight: 4 }}>
                          Gaps:
                        </span>
                        {explain.gaps.map((gap, idx) => (
                          <span key={`${gap}-${idx}`} style={GAP_CHIP_STYLE}>
                            {gap}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: '#98A2B3', marginTop: 4 }}>
                    AI explanation unavailable{explain?.status === 'timeout' ? ' (timed out)' : ''}.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions column. Shortlist/Declined tabs (Story 2b) show
          Apply/Restore; Batch/Search rows show Add/Decline, wired to real
          dispositions when the handlers are provided. Applicant mode
          (kan-119) shows the application instead — no buttons. */}
      <div style={ACTIONS_COL_STYLE}>
        {dispositionMode === 'applicants' ? (
          applicant ? (
            <>
              {/* kan-120: the AI fit score sits on the pill's line, the same
                  AiScoreBadge the Job Applicants tab uses (it hides itself
                  without the AI_MATCH_SCORING module). key = applicant id so a
                  reused row never shows another applicant's score. */}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                <span style={STATUS_PILL_STYLE}>{formatJobAppStatus(applicant.JobAppStatus)}</span>
                <AiScoreBadge
                  key={applicant.id}
                  jobApplicantId={applicant.id}
                  candidateId={row.candidateId}
                  aiScore={applicant.aiScore ?? undefined}
                  aiScoreReason={applicant.aiScoreReason ?? undefined}
                  aiScoredResumeId={applicant.aiScoredResumeId ?? undefined}
                  aiScoredAt={applicant.aiScoredAt ?? undefined}
                  onScoreUpdated={(s) => onApplicantScoreUpdated?.(row.candidateId, s)}
                />
              </div>
              <div style={{ fontSize: 12, color: '#667085' }}>
                {`Applied ${moment(applicant.createdAt).format('MMM D')}`}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 11, color: '#98A2B3' }}>
              {applicantLoaded ? 'Application not found' : 'loading application…'}
            </div>
          )
        ) : dispositionMode === 'shortlist' ? (
          <Button color="warning" size="sm" onClick={() => onApplyRow?.(row.candidateId)}>
            Apply
          </Button>
        ) : dispositionMode === 'declined' ? (
          <Button color="secondary" outline size="sm" onClick={() => onRestoreRow?.(row.candidateId)}>
            Restore
          </Button>
        ) : (
          <>
            <Button
              color="success"
              outline
              size="sm"
              disabled={!onShortlistRow}
              title={onShortlistRow ? undefined : 'Coming soon'}
              onClick={() => onShortlistRow?.(row.candidateId)}
            >
              Add
            </Button>
            <Button
              color="danger"
              outline
              size="sm"
              disabled={!onDeclineRow}
              title={onDeclineRow ? undefined : 'Coming soon'}
              onClick={() => onDeclineRow?.(row.candidateId)}
            >
              Decline
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CandidateRow;
